/**
 * Environment variable validation schema using Zod
 * @module lib/env/schema
 */

import { z } from 'zod';

/**
 * Schema for validating client-side environment variables
 */
export const envSchema = z.object({
  /**
   * Backend API base URL (required)
   * Must start with http:// or https://
   */
  VITE_API_URL: z
    .string()
    .url('VITE_API_URL must be a valid URL')
    .refine(
      (url) => url.startsWith('http://') || url.startsWith('https://'),
      'VITE_API_URL must start with http:// or https://'
    ),

  /**
   * Supported locales (optional, defaults to 'ar,en')
   */
  VITE_SUPPORTED_LOCALES: z
    .string()
    .optional()
    .default('ar,en')
    .transform((val) => val.split(',').map((l) => l.trim())),

  /**
   * Default locale (optional, defaults to 'ar')
   */
  VITE_DEFAULT_LOCALE: z
    .string()
    .optional()
    .default('ar')
    .refine(
      (locale) => ['ar', 'en'].includes(locale),
      'VITE_DEFAULT_LOCALE must be either "ar" or "en"'
    ),

  /**
   * Enable analytics (optional, defaults to false)
   */
  VITE_ENABLE_ANALYTICS: z
    .string()
    .optional()
    .default('false')
    .transform((val) => val === 'true'),
});

/**
 * Inferred TypeScript type from the schema
 */
export type Env = z.infer<typeof envSchema>;

/**
 * Validate environment variables and return typed result
 * Throws ZodError if validation fails
 */
export function validateEnv(): Env {
  return envSchema.parse({
    VITE_API_URL: import.meta.env.VITE_API_URL,
    VITE_SUPPORTED_LOCALES: import.meta.env.VITE_SUPPORTED_LOCALES,
    VITE_DEFAULT_LOCALE: import.meta.env.VITE_DEFAULT_LOCALE,
    VITE_ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS,
  });
}
